import React from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";

export const ProtectedRoute: React.FC<{ children: React.ReactNode; adminOnly?: boolean }> = ({
  children,
  adminOnly = false,
}) => {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="min-h-screen flex items-center justify-center bg-obsidian">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  const isPanel = user.role === "admin" || user.role === "staff";
  if (adminOnly && !isPanel) return <Navigate to="/portal" replace />;
  if (!adminOnly && isPanel) return <Navigate to="/admin" replace />;
  return <>{children}</>;
};

export const ROUTE_FOR: Record<string, string> = {
  dashboard: "/admin",
  users: "/admin/usuarios",
  redemptions: "/admin/canjes",
  catalog: "/admin/catalogo",
  rules: "/admin/puntos/reglas",
  campaigns: "/admin/campanas",
  subscriptions: "/admin/suscripciones",
  portal: "/admin/funcionalidades",
  team: "/admin/equipo",
};
const PERM_ORDER = ["dashboard", "users", "redemptions", "catalog", "subscriptions", "rules", "portal", "campaigns", "team"];

export const firstAllowedRoute = (perms: string[]) => {
  const first = PERM_ORDER.find((p) => perms.includes(p));
  return first ? ROUTE_FOR[first] : null;
};

// Sin permisos (o sin el de esta sección): se explica en pantalla en lugar de redirigir
// en silencio. Redirigir a /portal con cero permisos generaba un bucle con ProtectedRoute.
export function NoAccess({ section }: { section?: boolean }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const target = firstAllowedRoute(user?.permissions || []);
  return (
    <div className="card-obsidian p-10 max-w-xl mx-auto text-center mt-10" data-testid="no-access-page" role="alert">
      <ShieldOff className="w-10 h-10 text-slate-500 mx-auto mb-4" aria-hidden="true" />
      <h1 className="text-2xl font-serif font-medium mb-2">
        {section && target ? "No tienes acceso a esta sección" : "Todavía no tienes acceso al panel"}
      </h1>
      <p className="text-sm text-slate-600 mb-6">
        {section && target
          ? "Tu usuario no tiene permiso para ver esta sección. Si la necesitas, pídele al administrador principal que te la asigne en Equipo y permisos."
          : "Tu usuario no tiene ninguna sección asignada. Pídele al administrador principal que te asigne permisos en Equipo y permisos."}
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        {section && target ? (
          <Button asChild className="gold-gradient-bg text-white font-semibold rounded-full">
            <Link to={target}>Ir a mi primera sección</Link>
          </Button>
        ) : (
          <Button onClick={() => { logout(); navigate("/"); }} className="gold-gradient-bg text-white font-semibold rounded-full" data-testid="no-access-logout">
            Cerrar sesión
          </Button>
        )}
      </div>
    </div>
  );
}

export const RequirePermission: React.FC<{ perm: string; children: React.ReactNode }> = ({ perm, children }) => {
  const { user } = useAuth();
  if (!user) return null;
  const perms = user.permissions || [];
  if (!perms.includes(perm)) return <NoAccess section />;
  return <>{children}</>;
};
